import { lazy } from "react";
import { L, Navigate, type RouteDef } from "./shared";

// Core / utility pages (lazy)
const Portal = lazy(() => import("@/pages/Portal"));
const BrandGuide = lazy(() => import("@/pages/BrandGuide"));
const RolloverPortal = lazy(() => import("@/pages/RolloverPortal"));
const Terms = lazy(() => import("@/pages/Terms"));
const Privacy = lazy(() => import("@/pages/Privacy"));
const Pricing = lazy(() => import("@/pages/Pricing"));
const FAQPage = lazy(() => import("@/pages/FAQPage"));
const Glossary = lazy(() => import("@/pages/Glossary"));
const AdvisorInsights = lazy(() => import("@/pages/AdvisorInsights"));
const LongTermCare = lazy(() => import("@/pages/LongTermCare"));
const Reviews = lazy(() => import("@/pages/Reviews"));
const NewsUpdates = lazy(() => import("@/pages/NewsUpdates"));
const ArizonaRegulatoryLandscape = lazy(
  () => import("@/pages/ArizonaRegulatoryLandscape"),
);
const StartHere = lazy(() => import("@/pages/StartHere"));
const CaseStudiesHub = lazy(() => import("@/pages/CaseStudiesHub"));
const ResourceHub = lazy(() => import("@/pages/ResourceHub"));
const ToolsAssessmentsHub = lazy(() => import("@/pages/ToolsAssessmentsHub"));
const MethodologyResourcesHub = lazy(
  () => import("@/pages/MethodologyResourcesHub"),
);
const Questionnaire = lazy(() => import("@/pages/Questionnaire"));
const TrustComparison = lazy(() => import("@/pages/TrustComparison"));
const FindYourTrust = lazy(() => import("@/pages/FindYourTrust"));
const ProvisionGlossary = lazy(() => import("@/pages/ProvisionGlossary"));
const DisclosureCenter = lazy(() => import("@/pages/DisclosureCenter"));
const StrategyAssistantPage = lazy(
  () => import("@/pages/StrategyAssistantPage"),
);
const SeoRoadmap = lazy(() => import("@/pages/SeoRoadmap"));
const StrategicPartnerships = lazy(
  () => import("@/pages/StrategicPartnerships"),
);
const AboutAnthonyDamato = lazy(() => import("@/pages/AboutAnthonyDamato"));
const FoundersMessage = lazy(() => import("@/pages/FoundersMessage"));
const RealEstateStrategy = lazy(() => import("@/pages/RealEstateStrategy"));
const WealthPreservationScottsdale = lazy(
  () => import("@/pages/WealthPreservationScottsdale"),
);
const RetirementPlanningArizona = lazy(
  () => import("@/pages/RetirementPlanningArizona"),
);
const TrustEstateEducation = lazy(() => import("@/pages/TrustEstateEducation"));
const BusinessStrategyPhoenix = lazy(
  () => import("@/pages/BusinessStrategyPhoenix"),
);
const MesaCommercialFunding = lazy(
  () => import("@/pages/MesaCommercialFunding"),
);
const ChandlerBusinessSuccession = lazy(
  () => import("@/pages/ChandlerBusinessSuccession"),
);
const GlendaleBusinessContinuity = lazy(
  () => import("@/pages/GlendaleBusinessContinuity"),
);
const TempeExecutiveBenefits = lazy(
  () => import("@/pages/TempeExecutiveBenefits"),
);
const PeoriaRiskAssessment = lazy(() => import("@/pages/PeoriaRiskAssessment"));
const GilbertCommercialLending = lazy(
  () => import("@/pages/GilbertCommercialLending"),
);

export const coreRoutes: RouteDef[] = [
  {
    path: "/start-here",
    element: (
      <L>
        <StartHere />
      </L>
    ),
  },
  {
    path: "/questionnaire",
    element: (
      <L>
        <Questionnaire />
      </L>
    ),
  },
  {
    path: "/case-studies",
    element: (
      <L>
        <CaseStudiesHub />
      </L>
    ),
  },
  {
    path: "/resources",
    element: (
      <L>
        <ResourceHub />
      </L>
    ),
  },
  {
    path: "/portal",
    element: (
      <L>
        <Portal />
      </L>
    ),
  },
  {
    path: "/tools-assessments",
    element: (
      <L>
        <ToolsAssessmentsHub />
      </L>
    ),
  },
  {
    path: "/methodology-resources",
    element: (
      <L>
        <MethodologyResourcesHub />
      </L>
    ),
  },
  {
    path: "/brand",
    element: (
      <L>
        <BrandGuide />
      </L>
    ),
  },
  {
    path: "/rollover-portal",
    element: (
      <L>
        <RolloverPortal />
      </L>
    ),
  },
  {
    path: "/arizona-regulatory-landscape",
    element: (
      <L>
        <ArizonaRegulatoryLandscape />
      </L>
    ),
  },
  {
    path: "/terms",
    element: (
      <L>
        <Terms />
      </L>
    ),
  },
  {
    path: "/privacy",
    element: (
      <L>
        <Privacy />
      </L>
    ),
  },
  {
    path: "/b2b-consulting",
    element: <Navigate to="/where-srg-fits" replace />,
  },
  {
    path: "/wealth-preservation",
    element: <Navigate to="/wealth-preservation-strategy" replace />,
  },
  {
    path: "/pricing",
    element: (
      <L>
        <Pricing />
      </L>
    ),
  },
  {
    path: "/faq",
    element: (
      <L>
        <FAQPage />
      </L>
    ),
  },
  {
    path: "/glossary",
    element: (
      <L>
        <Glossary />
      </L>
    ),
  },
  {
    path: "/long-term-care",
    element: (
      <L>
        <LongTermCare />
      </L>
    ),
  },
  {
    path: "/insights",
    element: (
      <L>
        <AdvisorInsights />
      </L>
    ),
  },
  {
    path: "/business-succession",
    element: <Navigate to="/business-succession-planning" replace />,
  },
  {
    path: "/executive-benefits",
    element: <Navigate to="/executive-retention-planning" replace />,
  },
  {
    path: "/retirement-planning",
    element: <Navigate to="/business-owner-retirement-planning" replace />,
  },
  {
    path: "/estate-planning",
    element: <Navigate to="/business-owner-trust-coordination" replace />,
  },
  {
    path: "/real-estate-strategy",
    element: (
      <L>
        <RealEstateStrategy />
      </L>
    ),
  },
  {
    path: "/wealth-preservation-scottsdale",
    element: (
      <L>
        <WealthPreservationScottsdale />
      </L>
    ),
  },
  {
    path: "/buy-sell-agreements",
    element: <Navigate to="/buy-sell-agreement-funding" replace />,
  },
  {
    path: "/business-succession-arizona",
    element: <Navigate to="/locations/arizona" replace />,
  },
  {
    path: "/rollover-education",
    element: <Navigate to="/401k-rollover-education" replace />,
  },
  {
    path: "/key-person-insurance",
    element: <Navigate to="/key-person-risk-protection" replace />,
  },
  {
    path: "/commercial-funding",
    element: <Navigate to="/commercial-capital-solutions" replace />,
  },
  {
    path: "/trust-estate-education",
    element: (
      <L>
        <TrustEstateEducation />
      </L>
    ),
  },
  {
    path: "/business-strategy-phoenix",
    element: (
      <L>
        <BusinessStrategyPhoenix />
      </L>
    ),
  },
  {
    path: "/business-owner-financial-strategy-arizona",
    element: <Navigate to="/business-owner-financial-strategy" replace />,
  },
  {
    path: "/retirement-planning-arizona",
    element: (
      <L>
        <RetirementPlanningArizona />
      </L>
    ),
  },
  {
    path: "/mesa-commercial-funding",
    element: (
      <L>
        <MesaCommercialFunding />
      </L>
    ),
  },
  {
    path: "/chandler-business-succession",
    element: (
      <L>
        <ChandlerBusinessSuccession />
      </L>
    ),
  },
  {
    path: "/glendale-business-continuity",
    element: (
      <L>
        <GlendaleBusinessContinuity />
      </L>
    ),
  },
  {
    path: "/tempe-executive-benefits",
    element: (
      <L>
        <TempeExecutiveBenefits />
      </L>
    ),
  },
  {
    path: "/peoria-risk-assessment",
    element: (
      <L>
        <PeoriaRiskAssessment />
      </L>
    ),
  },
  {
    path: "/gilbert-commercial-lending",
    element: (
      <L>
        <GilbertCommercialLending />
      </L>
    ),
  },
  {
    path: "/strategic-partnerships",
    element: (
      <L>
        <StrategicPartnerships />
      </L>
    ),
  },
  {
    path: "/about-anthony-damato",
    element: (
      <L>
        <AboutAnthonyDamato />
      </L>
    ),
  },
  {
    path: "/founders-message",
    element: (
      <L>
        <FoundersMessage />
      </L>
    ),
  },
  {
    path: "/reviews",
    element: (
      <L>
        <Reviews />
      </L>
    ),
  },
  {
    path: "/news-updates",
    element: (
      <L>
        <NewsUpdates />
      </L>
    ),
  },
  {
    path: "/provision-glossary",
    element: (
      <L>
        <ProvisionGlossary />
      </L>
    ),
  },
  {
    path: "/trust-comparison",
    element: (
      <L>
        <TrustComparison />
      </L>
    ),
  },
  {
    path: "/find-your-trust",
    element: (
      <L>
        <FindYourTrust />
      </L>
    ),
  },
  {
    path: "/disclosure-center",
    element: (
      <L>
        <DisclosureCenter />
      </L>
    ),
  },
  {
    path: "/strategy-assistant",
    element: (
      <L>
        <StrategyAssistantPage />
      </L>
    ),
  },
  {
    path: "/seo-roadmap",
    element: (
      <L>
        <SeoRoadmap />
      </L>
    ),
  },
];
